<script lang="ts">
    import axios from 'axios';
    import Shgrid from '../../components/shgrid/shgrid.svelte';
    import type {Column} from '@/components/shgrid';
    import type {Customer, CustomerType} from "@/types/types";
    import {SvelteMap} from "svelte/reactivity";
    import type { Snippet } from 'svelte';
    import { PicklistBadge } from '@/components/ui/picklist-badge';
    import * as Table from '@/components/ui/table/index.js';

    type Row = Customer;

    type Props = {
        view : string;
        onRowClick?: (row: Row) => void;
        relatedData?: Record<string, any>;
        selectedCustomers?: SvelteMap<string, string> | undefined;
        onSubmitClick?: () => void;
        typeFilter?: CustomerType | null;
        hidePrimaryColumn?: boolean;
        title?: string | null;
        description?: string | null;
        headerSnippet?: Snippet;
        id?: string;
    };

    let {view, onRowClick, relatedData, selectedCustomers = $bindable(), onSubmitClick, typeFilter = null, hidePrimaryColumn = false, title = null, description = null, headerSnippet, id}: Props = $props();
    const getRowLink = (row: Row) => route('customers.show', {customer: row.id});
    const includeLink = view != 'related';

    // Show primary column only in job-related views (and can be explicitly hidden)
    const showPrimaryColumn = view === 'relatedList' && !hidePrimaryColumn;

    const primaryColumn: Column<Row> = {
        label: 'Primary',
        key: 'is_primary',
        cellRender: {
            type: 'text',
            label: (row: Row) => (row as any).is_primary ? '⭐' : '',
        },
        sortable: true,
        filter: {
            type: 'string',
        },
    };

    let cols = $state<Column<Row>[]>([
        ...(showPrimaryColumn ? [primaryColumn] : []),
        {
            label: 'Type',
            key: 'type',
            cellRender: {
                type: 'custom',
                snippet: typeSnippet,
                label: (row) => row.type_label ?? '',
            },
            sortable: true,
            filter: {
                type: 'select',
                options: [
                    { label: 'All', value: '' },
                    { label: 'Contact', value: 'contact' },
                    { label: 'Organisation', value: 'organisation' },
                ],
            },
        },
        {
            label: 'Name',
            key: 'name',
            cellRender: {
                type: 'text',
                label: (row) => row.display_name ?? row.name,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        // {
        //     label: 'Jobs',
        //     key: 'jobs',
        //     cellRender: {
        //         type: 'text',
        //         label: (row) => row.related_jobs.map((job) => job.title).join(", "),
        //         ...(includeLink && { link: getRowLink }),
        //     },
        //     sortable: true,
        //     filter: {
        //         type: 'string',
        //     },
        // },
        {
            label: 'Phone',
            key: 'phone',
            cellRender: {
                type: 'text',
                label: (row) => row.phone ?? "",
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Email',
            key: 'email',
            cellRender: {
                type: 'text',
                label: (row) => row.email,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Organisation',
            key: 'organisation_name',
            cellRender: {
                type: 'text',
                label: (row) => row.organisation_name,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Created At',
            key: 'created_at',
            cellRender: {
                type: 'date',
                label: (row) => row.created_at,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'date',
            },
        },
        {
            label: 'Last Updated',
            key: 'updated_at',
            cellRender: {
                type: 'date',
                label: (row) => row.updated_at,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'date',
            },
        }
    ]);
</script>

{#snippet typeSnippet(row: Row)}
    <Table.Cell>
        {#if row.type_label && row.type_color}
            <PicklistBadge label={row.type_label} color={row.type_color} />
        {:else if row.type_label}
            <span>{row.type_label}</span>
        {/if}
    </Table.Cell>
{/snippet}

<div {id}>
    <Shgrid
        fetcher={async ({ limit, offset, filters, sorter }) => {
            // Apply relatedData filters for relatedList, multi_related, and related views
            if ((view === 'relatedList' || view === 'multi_related' || view === 'related') && relatedData) {
                let relatedColumn = relatedData.column;
                let record_id = relatedData.record_id;
                filters = { ...filters, [relatedColumn]: record_id };
            }
            // Apply type filter from prop
            if (typeFilter) {
                filters = { ...filters, type: typeFilter };
            }
            const res = await axios.get(route('web-api.Customer.index', { limit, offset, filters, sorter }));
            return { data: res.data, error: null };
        }}
        limit={100}
        bind:columns={cols}
        {...(view === 'related' ? { onRowClick } : {})}
        bind:selected={selectedCustomers}
        {...(view === 'multi_related' ? { onSubmitClick } : {})}
        entityColumn="name"
        {title}
        {description}
        {headerSnippet}
    ></Shgrid>
</div>

